import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Building2, Building, BadgeCheck, Mail, Lock, 
  Eye, EyeOff, ArrowRight, AlertCircle, Check, 
  LogIn, UserPlus, Home, Sparkles
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { Alert, AlertDescription } from '../components/ui/alert';
import AgencyContactForm from '../components/register/AgencyContactForm';

const Login = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const loginButtonRef = useRef(null);

  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAgencyForm, setShowAgencyForm] = useState(false);
  const [activeCardHover, setActiveCardHover] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    remember: false
  });
  const [errors, setErrors] = useState({});

  const userTypes = [
    {
      id: 'cliente',
      label: 'Cliente',
      icon: User,
      description: 'Encuentra tu hogar ideal',
      color: 'blue',
      benefits: [
        'Guarda tus favoritos',
        'Contacta vendedores',
        'Historial de visitas',
        'Recomendaciones Personales'
      ]
    },
    {
      id: 'vendedor',
      label: 'Vendedor',
      icon: BadgeCheck,
      description: 'Vende y Alquila tus propiedades',
      color: 'purple',
      benefits: [
        'Primera Publicación GRATIS',
        'Estadísticas Completas',
        'Perfil Profesional',
        'Herramientas de promoción'
      ]
    },
    {
      id: 'inmobiliaria',
      label: 'Socio Inmobiliaria',
      icon: Building2,
      description: 'Crece tu negocio',
      color: 'emerald',
      benefits: [
        'Perfil empresarial',
        'Panel avanzado',
        'Destaca tus Propiedades',
        'Soporte Premium'
      ]
    },
    {
      id: 'constructora',
      label: 'Constructora',
      icon: Building,
      description: 'Desarrolla Proyectos',
      color: 'amber',
      benefits: [
        'Perfil corporativo',
        'Panel Especializado',
        'Estadísticas Avanzadas',
        'Soporte Prioritario 24/7'
      ]
    }
  ];

  useEffect(() => {
    if (user) {
      console.log("Usuario autenticado:", user);
      
      // Importante: Deshabilitamos este useEffect para usuarios que inician con Google
      // porque la redirección ya se maneja en handleGoogleSuccess
      const isGoogleLogin = localStorage.getItem('googleLoginInProgress');
      
      if (isGoogleLogin) {
        localStorage.removeItem('googleLoginInProgress');
        return; // No hacemos nada si es login con Google
      }
      
      // Para inicio de sesión normal (no Google):
      // Si es staff, redirigimos al dashboard
      if (user.tipo_usuario_id === 5) {
        console.log("Redirigiendo usuario staff al dashboard");
        navigate('/admin/dashboard');
      } else {
        // Para usuarios normales, comportamiento estándar
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl);
        } else {
          navigate('/mi_perfil');
        }
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setLoginData(prev => ({ ...prev, email: rememberedEmail }));
    }
  }, []);

  const handleLoginInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      passwordInputRef.current?.focus();
    }
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      loginButtonRef.current?.click();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      if (loginData.remember) {
        localStorage.setItem('rememberedEmail', loginData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      login(data.token, data.user);
      
      // La redirección se manejará en el useEffect que observa los cambios en 'user'
    } catch (error) {
      setErrors({ login: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      const decodedCredential = jwtDecode(credentialResponse.credential);
      const googleEmail = decodedCredential.email;
      
      console.log("Email de Google:", googleEmail);
    
      const checkResponse = await fetch(
        `https://backend-production-1786.up.railway.app/api/auth/check-email/${encodeURIComponent(googleEmail)}`
      );
      const checkResult = await checkResponse.json();
      
      console.log("Resultado de check-email:", checkResult);
    
      if (checkResult.exists) {
        console.log("Enviando solicitud de login con Google...");
        const loginResponse = await fetch('https://backend-production-1786.up.railway.app/api/auth/google-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: googleEmail,
            googleToken: credentialResponse.credential
          })
        });
    
        if (!loginResponse.ok) {
          const errorData = await loginResponse.json();
          throw new Error(errorData.error || 'Error en el inicio de sesión con Google');
        }
    
        const loginData = await loginResponse.json();
        console.log("Respuesta de login:", loginData);
        
        // Primero almacenamos la información del usuario en el contexto
        await login(loginData.token, loginData.user);
        
        // Después redirigimos basado en el tipo de usuario
        // Si es staff (tipo 5), va directamente al dashboard
        if (loginData.user && loginData.user.tipo_usuario_id === 5) {
          console.log("Redirigiendo a usuario staff al dashboard");
          setTimeout(() => navigate('/admin/dashboard'), 100);
        } else {
          // Para usuarios normales, usamos el comportamiento estándar
          const redirectUrl = localStorage.getItem('redirectAfterLogin');
          if (redirectUrl) {
            localStorage.removeItem('redirectAfterLogin');
            setTimeout(() => navigate(redirectUrl), 100);
          } else {
            setTimeout(() => navigate('/mi_perfil'), 100);
          }
        }
      } else {
        navigate('/register', { 
          state: { 
            googleEmail,
            googleToken: credentialResponse.credential 
          } 
        });
      }
    } catch (error) {
      console.error('Google Login Error:', error);
      setErrors({ login: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleUserTypeSelect = (type) => {
    if (type === 'constructora') {
      setShowAgencyForm(true);
    } else {
      navigate(`/register/${type}`);
    }
  };

  // Color mappings for user type cards
  const getColorClasses = (color, isActive) => {
    const colorMap = {
      blue: {
        bg: isActive ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800/50',
        hover: 'hover:bg-blue-50 dark:hover:bg-blue-900/20',
        border: isActive ? 'border-blue-400 dark:border-blue-500' : 'border-gray-100 dark:border-gray-700',
        hoverBorder: 'hover:border-blue-200 dark:hover:border-blue-600',
        icon: isActive ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500',
        hoverIcon: 'group-hover:text-blue-500 dark:group-hover:text-blue-400',
        title: isActive ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200',
        button: isActive ? 'bg-blue-500 dark:bg-blue-600' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-blue-500 dark:group-hover:bg-blue-600',
        iconBg: isActive ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700',
      },
      purple: {
        bg: isActive ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-white dark:bg-gray-800/50',
        hover: 'hover:bg-purple-50 dark:hover:bg-purple-900/20',
        border: isActive ? 'border-purple-400 dark:border-purple-500' : 'border-gray-100 dark:border-gray-700',
        hoverBorder: 'hover:border-purple-200 dark:hover:border-purple-600',
        icon: isActive ? 'text-purple-500 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500',
        hoverIcon: 'group-hover:text-purple-500 dark:group-hover:text-purple-400',
        title: isActive ? 'text-purple-700 dark:text-purple-300' : 'text-gray-800 dark:text-gray-200',
        button: isActive ? 'bg-purple-500 dark:bg-purple-600' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-purple-500 dark:group-hover:bg-purple-600',
        iconBg: isActive ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-100 dark:bg-gray-700',
      },
      emerald: {
        bg: isActive ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-white dark:bg-gray-800/50',
        hover: 'hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
        border: isActive ? 'border-emerald-400 dark:border-emerald-500' : 'border-gray-100 dark:border-gray-700',
        hoverBorder: 'hover:border-emerald-200 dark:hover:border-emerald-600',
        icon: isActive ? 'text-emerald-500 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500',
        hoverIcon: 'group-hover:text-emerald-500 dark:group-hover:text-emerald-400',
        title: isActive ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-800 dark:text-gray-200',
        button: isActive ? 'bg-emerald-500 dark:bg-emerald-600' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-emerald-500 dark:group-hover:bg-emerald-600',
        iconBg: isActive ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-700',
      },
      amber: {
        bg: isActive ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-white dark:bg-gray-800/50',
        hover: 'hover:bg-amber-50 dark:hover:bg-amber-900/20',
        border: isActive ? 'border-amber-400 dark:border-amber-500' : 'border-gray-100 dark:border-gray-700',
        hoverBorder: 'hover:border-amber-200 dark:hover:border-amber-600',
        icon: isActive ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500',
        hoverIcon: 'group-hover:text-amber-500 dark:group-hover:text-amber-400',
        title: isActive ? 'text-amber-700 dark:text-amber-300' : 'text-gray-800 dark:text-gray-200',
        button: isActive ? 'bg-amber-500 dark:bg-amber-600' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-amber-500 dark:group-hover:bg-amber-600',
        iconBg: isActive ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-gray-700',
      }
    };
    
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden flex items-center justify-center">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-blue-100 to-transparent dark:from-blue-900/30 dark:to-transparent opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-full h-64 bg-gradient-to-t from-pink-100 to-transparent dark:from-purple-900/30 dark:to-transparent opacity-40"></div>
        
        {/* Decorative circles */}
        <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-blue-200 dark:bg-blue-900/30 opacity-20 blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-purple-200 dark:bg-purple-900/30 opacity-20 blur-xl"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-emerald-200 dark:bg-emerald-900/30 opacity-20 blur-xl"></div>
      </div>

      <div className={`relative z-10 transition-all duration-500 w-full ${
        activeTab === 'register' ? 'max-w-7xl' : 'max-w-md'
      }`}>
        <div className="backdrop-blur-xl bg-white/95 dark:bg-gray-800/95 rounded-2xl shadow-xl dark:shadow-black/50 overflow-hidden border border-gray-100 dark:border-gray-700">
          {/* Tab selector */}
          <div className="flex border-b border-gray-100 dark:border-gray-700">
            <button
              className={`flex-1 py-4 px-6 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 relative
                ${activeTab === 'login' 
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
              onClick={() => setActiveTab('login')}
            >
              <LogIn className="w-4 h-4" />
              <span>Iniciar Sesión</span>
              {activeTab === 'login' && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500 dark:bg-blue-400" />
              )}
            </button>
            <button
              className={`flex-1 py-4 px-6 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 relative
                ${activeTab === 'register' 
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
              onClick={() => setActiveTab('register')}
            >
              <UserPlus className="w-4 h-4" />
              <span>Registrarse</span>
              {activeTab === 'register' && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500 dark:bg-blue-400" />
              )}
            </button>
          </div>

          <div className={`transition-all duration-500 ${
            activeTab === 'register' ? 'p-6 lg:p-8' : 'p-6'
          }`}>
            {activeTab === 'login' ? (
              <div className="max-w-md mx-auto space-y-6">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Home className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Bienvenido de nuevo</h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Ingresa tus credenciales para acceder a tu cuenta
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Email Input */}
                  <div className={`relative transition-all duration-300 ${
                    focusedInput === 'email' ? 'scale-[1.02]' : ''
                  }`}>
                    <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2 ml-1">
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" 
                            size={18} />
                      <input
                        ref={emailInputRef}
                        type="email"
                        name="email"
                        placeholder="tu@email.com"
                        value={loginData.email}
                        onChange={handleLoginInputChange}
                        onKeyDown={handleEmailKeyDown}
                        onFocus={() => setFocusedInput('email')}
                        onBlur={() => setFocusedInput(null)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl 
                                 bg-white dark:bg-gray-800/50 text-gray-900 dark:text-gray-100
                                 placeholder-gray-400 dark:placeholder-gray-500
                                 transition-all duration-300
                                 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400
                                 hover:border-blue-200 dark:hover:border-blue-600"
                      />
                    </div>
                  </div>

                  {/* Password Input */}
                  <div className={`relative transition-all duration-300 ${
                    focusedInput === 'password' ? 'scale-[1.02]' : ''
                  }`}>
                    <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2 ml-1">
                      Contraseña
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" 
                            size={18} />
                      <input
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={handleLoginInputChange}
                        onKeyDown={handlePasswordKeyDown}
                        onFocus={() => setFocusedInput('password')}
                        onBlur={() => setFocusedInput(null)}
                        className="w-full pl-10 pr-12 py-3 border border-gray-200 dark:border-gray-600 rounded-xl 
                                 bg-white dark:bg-gray-800/50 text-gray-900 dark:text-gray-100
                                 placeholder-gray-400 dark:placeholder-gray-500
                                 transition-all duration-300
                                 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400
                                 hover:border-blue-200 dark:hover:border-blue-600"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 
                                 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Remember me and Forgot Password */}
                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="remember"
                        checked={loginData.remember}
                        onChange={handleLoginInputChange}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-500 dark:text-blue-400
                                 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors
                                 bg-white dark:bg-gray-800"
                      />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">
                        Recordarme
                      </span>
                    </label>
                    <button 
                      onClick={() => navigate('/reset-password')}
                      className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 transition-colors hover:underline"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>

                  {/* Error Message */}
                  {errors.login && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                      <p>{errors.login}</p>
                    </div>
                  )}

                  {/* URL de redirección */}
                  {localStorage.getItem('redirectAfterLogin') && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 text-blue-700 dark:text-blue-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                      <ArrowRight className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                      <p>Después de iniciar sesión, volverás a la página anterior.</p>
                    </div>
                  )}

                  {/* Login Button */}
                  <button
                    ref={loginButtonRef}
                    onClick={handleLogin}
                    disabled={loading}
                    className="w-full bg-blue-600 dark:bg-blue-700 text-white 
                             py-3 rounded-xl font-medium transition-all duration-300
                             hover:bg-blue-700 dark:hover:bg-blue-600 hover:shadow-md dark:hover:shadow-black/25
                             active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed
                             flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        <span>Iniciando sesión...</span>
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5" />
                        <span>Iniciar Sesión</span>
                      </>
                    )}
                  </button>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200 dark:border-gray-600" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">O continuar con</span>
                    </div>
                  </div>

                  {/* Google Login */}
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setErrors({ login: 'Error en el inicio de sesión con Google' })}
                      width="300"
                      theme="filled_blue"
                      shape="pill"
                      size="large"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Elige tu tipo de cuenta</h2>
                  <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                    Selecciona el tipo de cuenta que mejor se adapte a tus necesidades para comenzar a disfrutar de todos los beneficios
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {userTypes.map(({ id, label, icon: Icon, description, benefits, color }) => {
                    const isActive = activeCardHover === id;
                    const colorClasses = getColorClasses(color, isActive);
                    
                    return (
                      <button
                        key={id}
                        onClick={() => handleUserTypeSelect(id)}
                        onMouseEnter={() => setActiveCardHover(id)}
                        onMouseLeave={() => setActiveCardHover(null)}
                        className={`
                          relative flex flex-col p-6 rounded-xl border-2 transition-all duration-300 backdrop-blur-sm
                          ${colorClasses.bg} ${colorClasses.hover} ${colorClasses.border} ${colorClasses.hoverBorder}
                          hover:shadow-lg dark:hover:shadow-black/25 active:scale-[0.98] group text-left h-[400px]
                        `}
                      >
                        <div className="mb-6">
                          <div className={`
                            w-16 h-16 rounded-xl transition-colors duration-300 mb-4
                            flex items-center justify-center backdrop-blur-sm
                            ${colorClasses.iconBg}
                          `}>
                            <Icon 
                              size={32}
                              className={`transition-colors duration-300
                                ${colorClasses.icon} ${colorClasses.hoverIcon}`}
                            />
                          </div>
                          
                          <h3 className={`text-xl font-bold mb-2 transition-colors duration-300
                            ${colorClasses.title}`}>
                            {label}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{description}</p>
                        </div>
                        
                        <div className="flex flex-col flex-1">
                          <div className="space-y-3 flex-1">
                            {benefits.map((benefit, index) => (
                              <div 
                                key={index} 
                                className="flex items-start text-sm text-gray-600 dark:text-gray-400"
                              >
                                <Check size={16} className={`mr-2 mt-0.5 flex-shrink-0 ${colorClasses.icon}`} />
                                <span>{benefit}</span>
                              </div>
                            ))}
                          </div>

                          <div className={`
                            mt-6 py-3 px-4 rounded-lg transition-all duration-300 
                            flex items-center justify-between backdrop-blur-sm
                            ${colorClasses.button}
                            text-white
                          `}>
                            <span className="font-medium">Comenzar</span>
                            <ArrowRight size={18} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showAgencyForm && <AgencyContactForm onClose={() => setShowAgencyForm(false)} />}
    </div>
  );
};

export default Login;